import { IAnnotation, IItemDelta, IItemReference, ISchemaObject, ItemOperation } from '@augloop/types-core';

/**
 * IN ACTIVE DEVELOPMENT
 *
 * Please note that this new workflow model is under active development, tracked by this work item:
 *
 * https://office.visualstudio.com/DefaultCollection/ISS/_workitems/edit/4849590
 *
 * It is not yet supported for general use, please contact PBaer and YutYe if you are interested in piloting.
 */

/**
 * Whether an item functions as an input item (provided to the workflow) or an output item (an annotation produced by
 * the workflow). Can be combined to indicate both (an input annotation that can be modified).
 */
export enum ItemRole {
    Input = 1,
    Output = 2
}

/**
 * Cardinality of workflow model items. Positive numbers are reserved for exact counts.
 */
export enum ItemCardinality {
    Single = 1, // Exactly one
    Any = -1, // Zero or more
    Optional = -2, // Zero or one
    Some = -3 // At least one
}

/**
 * Declares the expected schema for an item in the workflow model. The model items will be constrained to this schema,
 * i.e. workflows can assume that their input model will conform to it and do not need to perform runtime checks to
 * assure themselves that it does. Similarly, workflows may only produce or modify outputs that have been declared as
 * such in the schema.
 *
 * Implementation is tracked by https://office.visualstudio.com/DefaultCollection/ISS/_workitems/edit/4849590/
 */
export type WorkflowModelItemSchema = {
    /**
     * Optional name of this item schema (unique within the workflow model schema) to allow it to be referenced by multiple
     * other parent item schemas, including itself.
     */
    readonly name?: string;

    /**
     * The item type (e.g. TextTile.getTypeName()).
     */
    readonly type: string;

    /**
     * If workflow has enabled incremental execution, optionally indicates the IItemDelta-derived type to be used to
     * describe sub-item deltas for update operations.
     */
    readonly deltaType?: string;

    /**
     * Describes whether the item is a workflow input, output or both.
     */
    readonly role: ItemRole;

    /**
     * Describes how many instances of this item are expected under the same parent item. Input items that set this to
     * AtMostOne or Any are not considered essential for workflow execution and will be made available to workflows on
     * a best-effort basis only. If omitted, defaults to 1.
     */
    readonly cardinality?: number | ItemCardinality;

    /**
     * For input items, indicates whether the body content is unused and therefore does not need to be provided to the
     * workflow (useful if the item is only needed in order to attach child annotations, for example).
     */
    readonly noBody?: boolean;

    /**
     * For input items, indicates that changes to this item should not (by themselves) trigger invalidation of the workflow.
     * Implied to be true if noBody is true, since any changes would not be observable anyway.
     */
    readonly noInvalidation?: boolean;

    /**
     * True if the subtree below this item should be delay-loaded via IWorkflowModelItem.loadSubtree(). This is typically
     * required to be set on at least one item in the workflow model schema for successful execution on arbitrarily large
     * input models (i.e. that have item schemas with cardinality AtLeastOne or Any and the expected number of items is
     * greater than can be provided to workflows at a time). This also enables more efficient workflow execution on large
     * input models when the actual amount of data that is required is small but the exact subset of data to be fetched is
     * not known ahead of time.
     */
    readonly asyncBoundary?: boolean;

    /**
     * The child item schemas (if any), specified either by name or inline.
     */
    readonly children?: (string | WorkflowModelItemSchema)[];
};

/**
 * Serializable filter for single items that can be further subclassed to provide schema-specific filtering logic.
 */
export interface ISingleItemFilter extends ISchemaObject {
    /**
     * The item id to include, if itemType is unspecified.
     */
     readonly id?: string;

    /**
     * The item type to include, if id is unspecified.
     */
    readonly itemType?: string;
}

/**
 * Serializable filter on multiple items that can be further subclassed to provide schema-specific filtering logic.
 */
export interface IMultiItemFilter extends ISchemaObject {
    /**
     * The item id(s) to include, or all if undefined.
     */
     readonly ids?: string[];

    /**
     * The item type(s) to include, or all if undefined.
     */
    readonly itemTypes?: string[];
 }

/**
 * Synchronous callback for item filtering. Returns true if the given item should be included.
 */
export type ItemFilterCallback = (item: IWorkflowModelItem) => boolean;

/**
 * A filter for matching a sequential range of items (applied after the base filter).
 */
export interface ISequentialItemFilter extends IMultiItemFilter {
    /**
     * The index of the first item to load (defaults to 0 if not defined).
     */
    readonly start?: number;

    /**
     * The maximum number of items to load (defaults to all remaining items if not defined). The actual
     * number of items loaded may be less than this.
     *
     */
    readonly maxCount?: number;
}

export interface GridRange {
    readonly row: number;
    readonly col: number;
    readonly rows: number;
    readonly cols: number;
}

/**
 * A filter for matching a 2D range of items in a grid.
 */
export interface IGridSubtreeFilter extends IMultiItemFilter {
    ranges: GridRange[];
}

export type AnnotationOptions = {
    /**
     * Apply the annotation change and send it back to the session data model immediately,
     * without waiting for the workflow to be completed.
     */
    immediate?: boolean;
};

/**
 * Represents an item in the workflow model.
 *
 * Implementation is tracked by https://office.visualstudio.com/DefaultCollection/ISS/_workitems/edit/4849590/
 */
export interface IWorkflowModelItem {
    /**
     * Identifier for this item that is unique within the model and stable across workflow executions within the same session.
     * This must be treated as opaque by workflows, as its value depends on internal details of the host application and the
     * session data model implementation. Different hosts may have different internal structures for the same workflow model,
     * and the same host may change its structure over time.
     */
    readonly id: string;

    /**
     * If this is an input item and the workflow has enabled incremental execution, indicates whether this
     * item has been added, updated or deleted since the previous execution of the workflow.
     */
    readonly operation?: ItemOperation;

    /**
     * If the workflow has enabled incremental execution and this item was updated since the last execution,
     * optionally describes the update delta in an item-specific way.
     */
    readonly delta?: IItemDelta;

    /**
     * If this is an input item and the workflow has enabled additional context (e.g. dynamic text workflow), indicates
     * whether this item is part of the context-only portion of the input and thus not eligible for annotations (adding
     * child output items will throw an exception).
     */
    readonly isContext?: boolean;

    /**
     * True if retrieval of child or subtree items requires an async loadSubtree() call.
     */
    readonly ayncBoundary?: boolean;

    /**
     * Returns the item body, or undefined if either the workflow has enabled incremental execution and this item was
     * deleted since the last execution, or delete() has been called.
     */
    getBody<T extends ISchemaObject>(): T;

    /**
     * Returns the first parent (ancestor) item that matches the filter. If no filter is provided, returns the immediate
     * parent, or undefined if this is the root item.
     */
    getParentItem(filter?: ISingleItemFilter | ItemFilterCallback): IWorkflowModelItem;

    /**
     * Returns the body of the first parent (ancestor) item that matches the filter. If no filter is provided, returns the
     * immediate parent body, or undefined if this is the root item.
     */
    getParentItemBody<T extends ISchemaObject>(filter?: ISingleItemFilter | ItemFilterCallback): T;

    /**
     * Returns the first child item that is currently loaded and matches the filter. If no filter is provided, returns
     * the first loaded child item. If asyncBoundary is true, loadSubtree() must be called first to load the desired items
     * into memory.
     */
    getChildItem(filter?: ISingleItemFilter | ItemFilterCallback): IWorkflowModelItem;

     /**
      * Returns the body of the first child item that is currently loaded and matches the filter. If no filter is provided,
      * returns the first loaded child item body. If asyncBoundary is true, loadSubtree() must be called first to load the
      * desired items into memory.
      */
    getChildItemBody<T extends ISchemaObject>(filter?: ISingleItemFilter | ItemFilterCallback): T;

    /**
     * Returns the child items that are currently loaded and match the filter. If no filter is provided, returns
     * all loaded child items. If asyncBoundary is true, loadSubtree() must be called first to load the desired items into
     * memory.
     */
    getChildItems(filter?: IMultiItemFilter | ItemFilterCallback): IWorkflowModelItem[];

    /**
     * Returns the bodies of the child items that are currently loaded and match the filter. If no filter is provided,
     * returns all loaded child item bodies. If asyncBoundary is true, loadSubtree() must be called first to load the desired
     * items into memory.
     */
    getChildItemBodies<T extends ISchemaObject>(filter?: IMultiItemFilter | ItemFilterCallback): T[];

    /**
     * Returns the first itemswithin the subtree rooted by this item that is currently loaded and matches the filter. If no filter
     * is provided, returns the first loaded subtree item. If asyncBoundary is true, loadSubtree() must be called first to load the
     * desired item into memory.
     */
    getSubtreeItem(filter?: ISingleItemFilter | ItemFilterCallback): IWorkflowModelItem;

     /**
      * Returns the bodies of the first item within the subtree rooted by this item that is currently loaded and matches the filter.
      * If no filter is provided, returns the first loaded subtree item body. If asyncBoundary is true, loadSubtree() must be called
      * first to load the desired item into memory.
      */
    getSubtreeItemBody<T extends ISchemaObject>(filter?: ISingleItemFilter | ItemFilterCallback): T;

    /**
     * Returns the items within the subtree rooted by this item that are currently loaded and match the filter. If no filter
     * is provided, returns all loaded subtree items. If asyncBoundary is true, loadSubtree() must be called first to load the
     * desired items into memory.
     */
    getSubtreeItems(filter?: IMultiItemFilter | ItemFilterCallback): IWorkflowModelItem[];

    /**
     * Returns the bodies of the items within the subtree rooted by this item that are currently loaded and match the filter.
     * If no filter is provided, returns all loaded subtree item bodies. If asyncBoundary is true, loadSubtree() must be called
     * first to load the desired items into memory.
     */
    getSubtreeItemBodies<T extends ISchemaObject>(filter?: IMultiItemFilter | ItemFilterCallback): T[];

    /**
     * Adds an annotation (output) item as a child of this item and returns the new item.
     */
    addAnnotation<T extends IAnnotation>(body: T, options?: AnnotationOptions): IWorkflowModelItem;

    /**
     * Adds an annotation (output) item as a sibling of this item and returns the new item.
     */
    addSiblingAnnotation<T extends IAnnotation>(body: T, options?: AnnotationOptions): IWorkflowModelItem;

    /**
     * Updates the body of the current item, which must be an output annotation.
     */
    update<T extends IAnnotation>(body: T, options?: AnnotationOptions): void;

    /**
     * Deletes the current item, which must be an output annotation, and all of its subtree items.
     */
    delete(options?: AnnotationOptions): void;

    /**
     * If asyncBoundary is true, asynchronously loads the subtree items below the current item (that match the filter, if
     * provided; and, conversely, unloads any items in the subtree not matching the filter). After the load is complete,
     * the subtree items can be retrieved synchronously. Only applies to workflows that have explicitly opted-in to
     * async model loading in order to support arbitrarily large inputs, by setting asyncBoundary in the current item's
     * WorkflowModelItemSchema. No-op (and unnecessary) if asyncBoundary is not true.
     *
     * @param filters Item filter that defines the subset to be loaded. Callback filters are not supported here, as the
     * filter may execute remotely.
     *
     * @returns Promise that completes when the content is loaded. May be rejected with CoherencyError if a coherent
     * snapshot is no longer available.
     */
    loadSubtree(filter?: IMultiItemFilter): Promise<void>;
}

/**
 * Provides access to the workflow data model (as defined by the workflow model schema and workflow configuration)
 */
export interface IWorkflowModel {
    /**
     * The root item of the model.
     */
    readonly rootItem: IWorkflowModelItem;

    /**
     * Returns the first item that matches the filter. If no filter is provided, returns the root item. If no
     * match is found, returns undefined.
     */
    getItem(filter?: ISingleItemFilter | ItemFilterCallback): IWorkflowModelItem;

    /**
     * Returns all items that match the filter. If no filter is provided, returns all items. If no match is
     * found, returns an empty array.
     */
    getItems(filter?: IMultiItemFilter | ItemFilterCallback): IWorkflowModelItem[];

    /**
     * Returns the body of the first item that matches the filter. If no filter is provided, returns the body of
     * the root item. If no match is found, returns undefined.
     */
    getItemBody<T extends ISchemaObject>(filter?: ISingleItemFilter | ItemFilterCallback): T;

    /**
     * Returns the bodies of all items that match the filter. If no filter is provided, returns all item bodies.
     * If no match is found, returns an empty array.
     */
    getItemBodies<T extends ISchemaObject>(filter?: IMultiItemFilter | ItemFilterCallback): T[];

    /**
     * Returns an item via reference from another item. If not found, returns undefined.
     */
    getItemByReference(reference: IItemReference): IWorkflowModelItem;

    /**
     * Returns an item body via reference from another item. If not found, returns undefined.
     */
    getItemBodyByReference<T extends ISchemaObject>(reference: IItemReference): T;
}

/**
 * Exception generated by IWorkflowModelItem.loadSubtree() if the underlying data store no longer has a snapshot of
 * the requested model data that is coherent with the rest of the currently loaded model, i.e. there is a risk of
 * inconsistency between model items.
 */
export class CoherencyError extends Error {
    // The below code is ugly
    // But see: https://github.com/Microsoft/TypeScript/wiki/Breaking-Changes#extending-built-ins-like-error-array-and-map-may-no-longer-work
    /* eslint-disable-next-line @typescript-eslint/naming-convention */
    public __proto__: Error;
    public constructor(message: string = 'CoherencyError') {
        super(message);

        this.__proto__ = CoherencyError.prototype;
    }
}
